import React, { useContext, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Input } from 'reactstrap';
import { useBulkSurveysCreateMutation } from '../../api/survey-templates';
import { ROUTES } from '../../constants/routes';
import { generateUrl } from '../../routes/util';
import Dashboard, { DashboardSection } from '@g17eco/molecules/dashboard';
import { SubsidiarySelectionTable } from '../downloads/partials/SubsidiarySelectionTable';
import { updateFn } from '@g17eco/molecules/form';
import {
  HideField,
  OnChangeReportingProps,
  SurveyReportingSettings,
} from '@features/survey-configuration/partials/SurveyReportingSettings';
import { useGoBack } from './hooks/useGoBack';
import { Breadcrumbs } from '@g17eco/molecules/breadcrumbs';
import { TemplateContext, TemplateContextLoadedProps } from './TemplateContainer';
import { BulkSurveysFormData } from './types';
import { hasEmptyFields, hasInvalidTags, requiredFields } from './utils';
import { useAppDispatch } from '../../reducers';
import { addSiteAlert, SiteAlertColors } from '../../slice/siteAlertsSlice';
import { MergeTagsButton } from './partials/MergeTagsButton';
import { Loader } from '@g17eco/atoms/loader';
import { SURVEY } from '@constants/terminology';
import { BasicAlert } from '@g17eco/molecules/alert';
import { createEffectiveDate, isValidDeadlineScheduledDates, toFormScheduledDates } from '@features/survey-configuration/utils';
import { SurveyDeadlineReminderCollapse } from '@features/survey-deadline';
import { FormScheduledDate } from '../../types/survey';

const TemplateBulkSurveysCreateError = ({
  deadlineDate,
  scheduledDates,
}: {
  deadlineDate?: string;
  scheduledDates?: FormScheduledDate[];
}) => {
  if (!isValidDeadlineScheduledDates({ deadlineDate, scheduledDates })) {
    return <div className='text-sm text-ThemeDangerMedium text-right mt-2'>Error: Invalid deadline reminder</div>;
  }

  return null;
};

export const TemplateBulkSurveysCreate = () => {
  const history = useHistory();
  const { initiativeId = '', templateId } = useParams<{ initiativeId: string; templateId: string }>();
  const goBack = useGoBack(initiativeId);
  const dispatch = useAppDispatch();
  const { templateData } = useContext(TemplateContext) as TemplateContextLoadedProps;
  const [bulkSurveysCreate, { isLoading }] = useBulkSurveysCreateMutation();
  const initialState: BulkSurveysFormData = {
    ...templateData,
    scheduledDates: toFormScheduledDates(templateData.scheduledDates),
    surveyName: '',
    reportingLevels: [initiativeId],
  };
  const [form, setForm] = useState<BulkSurveysFormData>(initialState);
  const updateForm: updateFn = (update) => {
    if (update.code === 'deadlineDate' && !update.value) {
      setForm({ ...form, deadlineDate: undefined, scheduledDates: [] });
      return;
    }
    setForm({ ...form, [update.code]: update.value })
  };

  const handleChange = ({ option, code }: OnChangeReportingProps) => {
    return updateForm({ code, value: option?.value });
  };

  const handleSetSelectedIds = (ids: string[]) => {
    return updateForm({ code: 'reportingLevels', value: ids ?? [initiativeId] });
  }

  const setSurveyName = (value: string) => updateForm({ code: 'surveyName', value });

  const handleSubmit = () => {
    const effectiveDate = createEffectiveDate(form) ?? new Date();
    bulkSurveysCreate({ ...form, effectiveDate }).unwrap()
      .then((response) => {
        history.push(generateUrl(ROUTES.SURVEY_TEMPLATES_HISTORY, { initiativeId, templateId, historyId: response._id }));
      })
      .catch((e) => dispatch(addSiteAlert({
        content: e.message,
        color: SiteAlertColors.Danger,
      })))
  }

  const isDisabled =
    !form.reportingLevels ||
    form.reportingLevels.length === 0 ||
    hasEmptyFields(form, requiredFields) ||
    hasInvalidTags(form.surveyName) ||
    !isValidDeadlineScheduledDates(form);

  return (
    <Dashboard>
      <Breadcrumbs
        rootLabel='All report templates'
        rootUrl={generateUrl(ROUTES.SURVEY_TEMPLATES, { initiativeId })}
        breadcrumbs={[{ label: `Use report template - ${templateData.name}` }]}
      />
      <div className='mt-3 ms-3 mb-3 text-ThemeTextMedium'>
        <h4 className='mb-1 text-ThemeHeadingDark'>Create {SURVEY.PLURAL} using report template</h4>
        <span className='fw-bold'>Template name:</span> {templateData.name}
      </div>
      <DashboardSection paddingInternal={0}>
        {isLoading ? (
          <Loader relative />
        ) : (
          <>
            <BasicAlert type={'danger'}>{hasInvalidTags(form.surveyName) ? 'Merge tag does not exist' : ''}</BasicAlert>
            <h5 className='text-ThemeHeadingMedium m-0'>{SURVEY.CAPITALIZED_SINGULAR} settings</h5>
            <div className='mt-4 position-relative'>
              <h6 className='text-ThemeTextDark'>{SURVEY.CAPITALIZED_SINGULAR} name</h6>
              <Input
                className='border-ThemeBorderDefault'
                placeholder={`e.g. December 2022 annual ${SURVEY.SINGULAR}`}
                value={form.surveyName}
                onChange={(el) => setSurveyName(el.target.value)}
                data-testid='survey-template-report-name-input'
              />
              <MergeTagsButton surveyName={form.surveyName} updateName={setSurveyName} />
            </div>
            <SurveyReportingSettings form={form} onChange={handleChange} hideFields={[HideField.BlueprintVersion]} />
            <SurveyDeadlineReminderCollapse
              deadlineDate={form.deadlineDate}
              scheduledDates={form.scheduledDates}
              updateForm={updateForm}
            />
            <h5 className='mt-4'>Select subsidiaries</h5>
            <SubsidiarySelectionTable initiativeId={initiativeId} handleSetSelectedIds={handleSetSelectedIds} />
            <div className='mt-4 text-right'>
              <Button color='link' onClick={goBack} className='mr-3'>
                Cancel
              </Button>
              <Button
                color='primary'
                className='submit-button'
                disabled={isDisabled}
                onClick={handleSubmit}
                data-testid='survey-template-bulk-create-btn'
              >
                Create {SURVEY.PLURAL}
              </Button>
            </div>
            <TemplateBulkSurveysCreateError deadlineDate={form.deadlineDate} scheduledDates={form.scheduledDates} />
          </>
        )}
      </DashboardSection>
    </Dashboard>
  );
};
